import { useState } from 'react'
import type { OrderDTO } from '@wf/shared'
import { ApiError } from '../api/client'
import { PRIO_OPTIONS } from './labels'
import { usePatchOrder } from './useOrders'

/**
 * Auftragsdaten nach der Annahme korrigieren: Priorität, geplanter Termin, Notiz. Alle drei gehen in EINEM
 * OCC-Patch raus (eine `version`, ein Versions-Sprung) statt drei Einzel-Schreibvorgängen. Gespeichert wird
 * nur bei echter Änderung (dirty), und nur die geänderten Felder werden gesendet. Felder, die der Server
 * bewusst NICHT übernommen hat (`skipped[]` — Recht fehlt oder nach Rechnung/Freigabe GoBD-gesperrt) und
 * ein 409-Konflikt werden ehrlich angezeigt; der Hook lädt bei Fehler den frischen Stand nach.
 */
export function OrderMetaEdit({ order }: { order: OrderDTO }) {
  const patch = usePatchOrder(order.id)
  const [prio, setPrio] = useState(order.prio)
  const [scheduledDate, setScheduledDate] = useState(order.scheduledDate ?? '')
  const [note, setNote] = useState(order.note ?? '')

  const nextDate = scheduledDate || null
  const nextNote = note.trim() || null
  const dirty = prio !== order.prio || nextDate !== order.scheduledDate || nextNote !== order.note

  const skipped = patch.data?.skipped ?? []
  const conflict = patch.error instanceof ApiError && patch.error.status === 409 ? patch.error.message : null
  const otherError = patch.error instanceof ApiError && patch.error.status !== 409 ? patch.error.message : null

  function save() {
    if (!dirty) return
    // Nur die tatsächlich geänderten Felder senden — so überschreibt ein leeres Feld nichts versehentlich
    // und der Server-skip bleibt auf das beschränkt, was wir wirklich ändern wollten.
    patch.mutate({
      version: order.version,
      ...(prio !== order.prio ? { prio } : {}),
      ...(nextDate !== order.scheduledDate ? { scheduledDate: nextDate } : {}),
      ...(nextNote !== order.note ? { note: nextNote } : {}),
    })
  }

  return (
    <section className="wf-orderedit">
      <span className="wf-label wf-sectiontitle">Auftragsdaten</span>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Priorität</span>
          <select className="wf-input" value={prio} onChange={(event) => setPrio(event.target.value)}>
            {PRIO_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="wf-field">
          <span className="wf-label">Termin</span>
          <input className="wf-input" type="date" value={scheduledDate} onChange={(event) => setScheduledDate(event.target.value)} />
        </label>
      </div>
      <label className="wf-field">
        <span className="wf-label">Notiz</span>
        <textarea className="wf-input wf-textarea" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>

      <div className="wf-orderedit__actions">
        <button type="button" className="wf-btn wf-btn--ghost" disabled={!dirty || patch.isPending} onClick={save}>
          Speichern
        </button>
      </div>

      {skipped.length > 0 ? (
        <p className="warn" role="status">
          Nicht übernommen: {skipped.join(', ')} — Recht fehlt oder nach Rechnung/Freigabe gesperrt.
        </p>
      ) : null}
      {conflict ? (
        <p className="warn" role="alert">
          Konflikt: {conflict} – der aktuelle Stand wurde neu geladen.
        </p>
      ) : null}
      {otherError ? (
        <p className="error" role="alert">
          {otherError}
        </p>
      ) : null}
    </section>
  )
}
